package com.islamicmedia.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.islamicmedia.site.components.CARD_ELEMENT_ID
import com.islamicmedia.site.components.cardErrorMessage
import com.islamicmedia.site.components.confirmCardSetup
import com.islamicmedia.site.components.installStripeCardGlue
import com.islamicmedia.site.components.mountCardElement
import com.islamicmedia.site.theme.CardElevatedShadow
import com.islamicmedia.site.theme.Gray500
import com.islamicmedia.site.theme.Gray900
import com.islamicmedia.site.theme.GradientPrimary
import com.islamicmedia.site.theme.PageBg
import com.islamicmedia.site.theme.ScoreRed
import com.varabyte.kobweb.core.Page
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.url.URLSearchParams

/**
 * Standalone "update your card" flow, opened by the mobile app in its in-app browser from a push
 * or email link. Card entry happens entirely against Stripe (see StripeCardGlue.kt) — this page
 * never talks to our own server, so there is nothing here that needs its own auth.
 */
@Page("/update-card")
@Composable
fun UpdateCardPage() {
    var parsed by remember { mutableStateOf(false) }
    var clientSecret by remember { mutableStateOf<String?>(null) }
    var publishableKey by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        // The SetupIntent secret and publishable key ride in the URL FRAGMENT (after '#'), never
        // the query string. A fragment is never sent in the request line to any server (this
        // page's own load included), so it never ends up in an access log, a CDN log, or a
        // Referer header the way a "?cs=" query param would. URLSearchParams both splits the
        // "key=value&key2=value2" shape and percent-decodes each value for us.
        val params = URLSearchParams(window.location.hash.removePrefix("#"))
        clientSecret = params.get("cs")?.takeIf { it.isNotBlank() }
        publishableKey = params.get("pk")?.takeIf { it.isNotBlank() }
        parsed = true
    }

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("min-height", "100vh")
            backgroundColor(PageBg)
            padding(24.px)
            property("box-sizing", "border-box")
            property("font-family", "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif")
        }
    }) {
        // Wait for the (synchronous, same-tick) hash parse before deciding what to show, so the
        // expired-link message never flashes for a link that is actually fine.
        if (!parsed) return@Div

        val cs = clientSecret
        val pk = publishableKey
        Div(attrs = { style { cardShell() } }) {
            if (cs == null || pk == null) {
                H1(attrs = { style { cardHeading() } }) { Text("Update your card") }
                P(attrs = { style { cardBody() } }) {
                    Text("This link has expired. Open the app and try again.")
                }
            } else {
                UpdateCardForm(pk, cs)
            }
        }
    }
}

@Composable
private fun UpdateCardForm(publishableKey: String, clientSecret: String) {
    var mountStatus by remember { mutableStateOf<String?>(null) }
    var confirming by remember { mutableStateOf(false) }
    var confirmError by remember { mutableStateOf<String?>(null) }
    val ready = mountStatus == "ok"

    LaunchedEffect(Unit) {
        installStripeCardGlue()
        mountCardElement(publishableKey, clientSecret) { status -> mountStatus = status }
    }

    H1(attrs = { style { cardHeading() } }) { Text("Update your card") }
    P(attrs = { style { cardBody(); marginBottom(20.px) } }) {
        Text("Your new card will be used for your next renewal.")
    }

    // Present unconditionally, before mountCardElement above ever resolves: Stripe's
    // element.mount('#id') looks the node up synchronously and fails silently if it isn't in the
    // DOM yet, so this can't be gated behind the "ok" status that call itself produces.
    Div(attrs = {
        id(CARD_ELEMENT_ID)
        style {
            minHeight(44.px)
            marginBottom(16.px)
            property("display", if (ready) "block" else "none")
        }
    })

    when (val status = mountStatus) {
        null -> P(attrs = { style { cardBody(); color(Gray500) } }) { Text("Loading…") }
        "ok" -> {}
        else -> P(attrs = { style { cardBody(); color(ScoreRed) } }) { Text(cardErrorMessage(status)) }
    }

    confirmError?.let { err ->
        P(attrs = { style { cardBody(); color(ScoreRed); marginTop(8.px) } }) { Text(err) }
    }

    Button(attrs = {
        val enabled = ready && !confirming
        if (enabled) {
            onClick {
                confirming = true
                confirmError = null
                val returnUrl = "${window.location.origin}/card-updated"
                confirmCardSetup(returnUrl) { status ->
                    when {
                        status.startsWith("ok:") -> {
                            val setupIntentId = status.removePrefix("ok:")
                            window.location.href = "/card-updated?setup_intent=$setupIntentId"
                        }
                        status == "redirect" -> {
                            // Stripe already navigated the tab away for 3-D Secure and will
                            // return to returnUrl itself. Nothing more to do on this page.
                        }
                        else -> {
                            confirmError = cardErrorMessage(status)
                            confirming = false
                        }
                    }
                }
            }
        } else {
            disabled()
        }
        style {
            marginTop(12.px)
            width(100.percent)
            padding(14.px, 20.px)
            fontSize(16.px)
            fontWeight(700)
            property("border", "none")
            borderRadius(12.px)
            property("cursor", if (enabled) "pointer" else "default")
            property("opacity", if (enabled) "1" else "0.6")
            property("background", GradientPrimary)
            color(Color.white)
        }
    }) {
        Text(if (confirming) "Saving…" else "Save card")
    }
}

private fun StyleScope.cardShell() {
    backgroundColor(Color.white)
    borderRadius(24.px)
    property("box-shadow", CardElevatedShadow)
    padding(28.px)
    property("width", "min(94%, 420px)")
    property("box-sizing", "border-box")
}

private fun StyleScope.cardHeading() {
    property("margin", "0 0 8px")
    fontSize(22.px)
    fontWeight(700)
    color(Gray900)
}

private fun StyleScope.cardBody() {
    property("margin", "0")
    fontSize(15.px)
    lineHeight(1.5.em)
    color(Gray500)
}
